import type { TFunction } from 'i18next';

const RELATIVE_TIME_FORMATTER_CACHE = new Map<string, Intl.RelativeTimeFormat>();

function getRelativeTimeFormatter(language: string): Intl.RelativeTimeFormat {
    const key = language || 'en';
    let formatter = RELATIVE_TIME_FORMATTER_CACHE.get(key);
    if (!formatter) {
        formatter = new Intl.RelativeTimeFormat(key, { numeric: 'auto' });
        RELATIVE_TIME_FORMATTER_CACHE.set(key, formatter);
    }
    return formatter;
}

export function formatDate(isoString: string, language: string, t: TFunction): string {
    const rtf = getRelativeTimeFormatter(language);
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    const absDays = Math.abs(diffDays);

    if (absDays < 45) {
        return rtf.format(diffDays, 'day');
    }

    const diffMonths = Math.round(diffDays / 30);
    const years = Math.trunc(diffMonths / 12);
    const monthsRemainder = diffMonths % 12;

    if (years === 0) {
        return rtf.format(diffMonths, 'month');
    }

    const yearPart = rtf.format(years, 'year');

    if (monthsRemainder === 0) {
        return yearPart;
    }

    const connector = t('common:relativeDate.yearMonthConnector');
    const monthPhrase = t('common:relativeDate.monthsCount', {
        count: Math.abs(monthsRemainder),
    });

    return `${yearPart}${connector}${monthPhrase}`;
}

export function nextCycleReset(period: string | undefined): string {
    if (!period) return '-';
    const match = /^(\d{4})-(\d{2})$/.exec(period);
    if (!match) return '-';
    const year = Number(match[1]);
    const month = Number(match[2]);
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const date = new Date(Date.UTC(nextYear, nextMonth - 1, 1));
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
}

/** Absolute date and time in the UI language, or '-' for an unparsable value. */
export function formatDateTime(isoString: string, language: string): string {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat(language || 'th', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(date);
}
